import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { api, setTrackToken } from "@/lib/api";

// Shopper (Google sign-in) API calls carry the Clerk session token; admin auth is separate.
export const useShopperApi = () => {
  const { getToken } = useAuth();
  return async (method, url, data) => {
    const token = await getToken();
    return api.request({ method, url: `/shopper${url}`, data, headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
  };
};

export const useSaved = () => {
  const { isSignedIn, userId } = useAuth();
  const [busy, setBusy] = useState(false);
  const call = useShopperApi();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["shopper-saved", userId], enabled: !!isSignedIn, queryFn: () => call("get", "/saved").then((r) => r.data) });
  const ids = new Set(q.data?.ids || []);
  const toggle = async (dealId) => {
    setBusy(true);
    try {
      await call(ids.has(dealId) ? "delete" : "post", `/saved/${dealId}`);
      await qc.invalidateQueries({ queryKey: ["shopper-saved", userId] });
      return !ids.has(dealId);
    } finally { setBusy(false); }
  };
  return { isSignedIn: !!isSignedIn, ids, busy, items: q.data?.items || [], loading: q.isLoading, toggle };
};

// Adds the signed-in shopper's private tracking token to outgoing deal links (/api/go/...) at click time.
export const useTrackLinks = () => {
  const call = useShopperApi();
  const { isSignedIn, userId } = useAuth();
  const q = useQuery({ queryKey: ["shopper-profile", userId], enabled: !!isSignedIn, staleTime: 600000, queryFn: async () => (await call("get", "/profile")).data });
  const token = q.data?.track_token;
  useEffect(() => {
    if (!token) return undefined;
    setTrackToken(token);
    const onClick = (e) => {
      const a = e.target.closest?.("a[href*='/api/go/']");
      if (!a) return;
      const u = new URL(a.href);
      u.searchParams.set("t", token);
      a.href = u.toString();
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("auxclick", onClick, true);
    return () => { setTrackToken(""); document.removeEventListener("click", onClick, true); document.removeEventListener("auxclick", onClick, true); };
  }, [token]);
  return q;
};
